// SIMPLE Markdown, rendered safely (Session ASQ, Stream G, Item 5). The ONE renderer for the privacy notice: the public
// /privacy page and the live preview in Settings both use it, so what the admin previews is what a customer reads.
//
// What it understands, and nothing more:
//   - "# Heading" and "## Heading" (a hash, then a space) at the start of a line;
//   - paragraphs, separated by a blank line. Lines inside a paragraph run together as one, as in any Markdown, so
//     wording hard-wrapped in a document (docs/gdpr/PRIVACY-NOTICE-DRAFT.md is) reads as flowing text rather than
//     breaking mid-sentence; for a separate line, leave a blank line or use a list;
//   - bullet lists ("- item" or "* item") and numbered lists ("1. item" or "1) item"); a line that follows an item
//     and is not itself a new item or heading continues that item (a wrapped item stays one item);
//   - **bold**;
//   - bare https:// links, which open in a new tab.
// EVERYTHING ELSE IS TEXT. "<script>", raw HTML, "### three hashes", "[text](url)", an http:// or javascript: address:
// all shown exactly as typed. The output is React elements built from parsed text, never dangerouslySetInnerHTML, so
// React escapes every character a person typed and no markup in the text can become markup on the page.
import type { CSSProperties, ReactNode } from "react";

export type MdInline = { kind: "text"; text: string } | { kind: "bold"; children: MdInline[] } | { kind: "link"; href: string };

export type MdBlock =
  | { kind: "heading"; level: 1 | 2; inline: MdInline[] }
  | { kind: "paragraph"; inline: MdInline[] }
  | { kind: "list"; ordered: boolean; start: number; items: MdInline[][] };

const HEADING = /^(#{1,2})[ \t]+(.+?)[ \t#]*$/;
const BULLET = /^[ \t]*[-*][ \t]+(.+)$/;
const NUMBERED = /^[ \t]*(\d{1,9})[.)][ \t]+(.+)$/;
// A bare https address: stops at whitespace, and never includes a quote or an angle bracket.
const HTTPS = /https:\/\/[^\s<>"'`]+/g;
// Punctuation that ends a sentence rather than the address ("see https://ico.org.uk.").
const TRAILING = /[.,;:!?]+$/;

// Links: an https address, with trailing sentence punctuation (and an unmatched closing bracket) left outside it.
function linkify(text: string): MdInline[] {
  const out: MdInline[] = [];
  let last = 0;
  for (const m of text.matchAll(HTTPS)) {
    let href = m[0];
    const trail = href.match(TRAILING)?.[0] ?? "";
    href = href.slice(0, href.length - trail.length);
    if (href.endsWith(")") && !href.includes("(")) href = href.slice(0, -1);
    if (href.length <= "https://".length) continue;
    const start = m.index!;
    if (start > last) out.push({ kind: "text", text: text.slice(last, start) });
    out.push({ kind: "link", href });
    last = start + href.length;
  }
  if (last < text.length) out.push({ kind: "text", text: text.slice(last) });
  return out;
}

export function parseInline(text: string): MdInline[] {
  const out: MdInline[] = [];
  const bold = /\*\*(.+?)\*\*/g;
  let last = 0;
  for (const m of text.matchAll(bold)) {
    if (m.index! > last) out.push(...linkify(text.slice(last, m.index)));
    out.push({ kind: "bold", children: linkify(m[1]!) });
    last = m.index! + m[0].length;
  }
  if (last < text.length) out.push(...linkify(text.slice(last)));
  return out;
}

// A block being read: its text is gathered line by line and only parsed for bold and links once it is complete, so a
// bold phrase or an address that a hard wrap split across two lines still reads as one.
type Pending =
  | { kind: "paragraph"; text: string }
  | { kind: "list"; ordered: boolean; start: number; items: string[] };

export function parseSimpleMarkdown(source: string): MdBlock[] {
  const blocks: MdBlock[] = [];
  let current: Pending | null = null;
  const close = () => {
    const c = current as Pending | null;
    if (c?.kind === "paragraph") blocks.push({ kind: "paragraph", inline: parseInline(c.text) });
    if (c?.kind === "list") blocks.push({ kind: "list", ordered: c.ordered, start: c.start, items: c.items.map(parseInline) });
    current = null;
  };
  for (const raw of source.replace(/\r\n?/g, "\n").split("\n")) {
    const line = raw.replace(/\s+$/, "");
    if (line.trim() === "") {
      close();
      continue;
    }
    const heading = line.match(HEADING);
    if (heading) {
      close();
      blocks.push({ kind: "heading", level: heading[1]!.length as 1 | 2, inline: parseInline(heading[2]!) });
      continue;
    }
    const bullet = line.match(BULLET);
    const numbered = bullet ? null : line.match(NUMBERED);
    if (bullet || numbered) {
      const ordered = !!numbered;
      const itemText = bullet ? bullet[1]! : numbered![2]!;
      const cur = current as Pending | null;
      if (cur && cur.kind === "list" && cur.ordered === ordered) {
        cur.items.push(itemText.trim());
      } else {
        close();
        current = { kind: "list", ordered, start: numbered ? Number(numbered[1]) : 1, items: [itemText.trim()] };
      }
      continue;
    }
    const cur = current as Pending | null;
    if (cur && cur.kind === "list") {
      // A wrapped list item: the line continues the item above it.
      cur.items[cur.items.length - 1] = `${cur.items[cur.items.length - 1]} ${line.trim()}`;
    } else if (cur && cur.kind === "paragraph") {
      cur.text = `${cur.text} ${line.trim()}`;
    } else {
      current = { kind: "paragraph", text: line.trim() };
    }
  }
  close();
  return blocks;
}

export interface SimpleMarkdownClasses {
  heading1: string;
  heading2: string;
  paragraph: string;
  list: string;
  bulletList: string;
  numberedList: string;
  item: string;
  link: string;
  bold: string;
}

// Structure only; colour comes from the surface the text sits on (the public page's light card, or the office theme).
const DEFAULT_CLASSES: SimpleMarkdownClasses = {
  heading1: "mt-6 mb-2 text-xl font-bold first:mt-0",
  heading2: "mt-5 mb-2 text-lg font-bold first:mt-0",
  paragraph: "my-3 leading-relaxed",
  list: "my-3 space-y-1 pl-6 leading-relaxed",
  bulletList: "list-disc",
  numberedList: "list-decimal",
  item: "",
  link: "underline break-all",
  bold: "font-bold",
};

function renderInline(parts: MdInline[], classes: SimpleMarkdownClasses, linkStyle: CSSProperties | undefined): ReactNode[] {
  return parts.map((p, i) => {
    if (p.kind === "text") return p.text;
    if (p.kind === "bold") {
      return (
        <strong key={i} className={classes.bold}>
          {renderInline(p.children, classes, linkStyle)}
        </strong>
      );
    }
    return (
      <a key={i} href={p.href} target="_blank" rel="noopener noreferrer" className={classes.link} style={linkStyle}>
        {p.href}
      </a>
    );
  });
}

export function SimpleMarkdown({
  text,
  headingLevel = 2,
  classes,
  headingStyle,
  linkStyle,
}: {
  text: string;
  // The HTML level a "#" heading renders at ("##" is one lower). The public page has its own h1, so "#" is an h2 there.
  headingLevel?: 1 | 2 | 3 | 4;
  classes?: Partial<SimpleMarkdownClasses>;
  headingStyle?: CSSProperties;
  linkStyle?: CSSProperties;
}) {
  const c: SimpleMarkdownClasses = { ...DEFAULT_CLASSES, ...classes };
  const blocks = parseSimpleMarkdown(text);
  return (
    <div data-simple-markdown="">
      {blocks.map((b, i) => {
        if (b.kind === "heading") {
          const level = Math.min(6, headingLevel + (b.level - 1));
          const Tag = `h${level}` as "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
          return (
            <Tag key={i} className={b.level === 1 ? c.heading1 : c.heading2} style={headingStyle}>
              {renderInline(b.inline, c, linkStyle)}
            </Tag>
          );
        }
        if (b.kind === "list") {
          const items = b.items.map((item, j) => (
            <li key={j} className={c.item}>
              {renderInline(item, c, linkStyle)}
            </li>
          ));
          return b.ordered ? (
            <ol key={i} start={b.start} className={`${c.list} ${c.numberedList}`}>
              {items}
            </ol>
          ) : (
            <ul key={i} className={`${c.list} ${c.bulletList}`}>
              {items}
            </ul>
          );
        }
        return (
          <p key={i} className={c.paragraph}>
            {renderInline(b.inline, c, linkStyle)}
          </p>
        );
      })}
    </div>
  );
}
